import { LitElement, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { distinctUntilChanged, filter, fromEvent, map, takeUntil, timer } from 'rxjs'
import style from './textarea.scss?inline'
import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'

import { FieldActionsController } from '../field-actions'
import { fieldChromeClasses } from '../input/input-chrome'

/**
 * Textarea with auto-resize and form integration.
 *
 * @slot actions - A CONTROL that acts on this field's value, rendered inside the
 *   field's own chrome. A multi-line field has no trailing edge to pin it to —
 *   the box grows, and reserving inline room on every wrapped line to clear a
 *   corner control would cost the field its measure — so it gets a row of its
 *   own below the text, trailing-aligned, inside the same border. Text never
 *   runs under it and it never moves as the box grows. It follows the field in
 *   tab order. On a field narrower than `FIELD_ACTIONS_COMPACT_WIDTH` the field
 *   sets `compact` on whatever is in the slot; `<schmancy-button compact>`
 *   renders icon-only and keeps its label as its accessible name.
 *
 *   While the slot is empty the field renders exactly the box it always did —
 *   chrome on the `<textarea>` itself. A filled slot moves that chrome onto a
 *   wrapper (`fieldChromeClasses`' `focus-within` variant, which exists for
 *   precisely this: a box whose focus lives on a child) so the row sits inside
 *   the border rather than under it.
 *
 * @csspart actions - The row holding the `actions` slot.
 */
@customElement('schmancy-textarea')
export default class SchmancyTextarea extends HostAriaLabelMixin(SchmancyFormField(unsafeCSS(style))) {
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	// `formAssociated`, `internals`, `name`, `label`, `required`, `disabled`,
	// `error`, `validationMessage`, `hint`, `id`, `validateOn`, `touched`,
	// `dirty`, `submitted`, `markTouched`, `markSubmitted`, `setCustomValidity`,
	// `formResetCallback`, `formDisabledCallback` — all from the mixin.
	textareaRef = createRef<HTMLTextAreaElement>()

	private readonly a11yId = `schmancy-textarea-${Math.random().toString(36).slice(2, 10)}`

	/** Owns the `actions` slot's shared half — see `../field-actions`. */
	private readonly fieldActions = new FieldActionsController(this)

	@property() placeholder = ''

	/** Narrowed from the mixin's wide union to the textarea-specific `string` type. */
	@property({ type: String, reflect: true }) override value: string = ''

	@property({ type: Number })
	public minlength: number | undefined

	@property({ type: Number })
	public maxlength!: number

	@property({ type: Number }) cols = 20

	/** When not set, the textarea auto-sizes to fit its content via field-sizing: content. */
	@property({ type: Number }) rows: number | undefined

	/**
	 * Bounds the field against the viewport (`60dvh` default, tunable via
	 * `--schmancy-textarea-fill-max-height`) and scrolls its own overflow
	 * internally — a self-contained cap, never a `height: 100%` chain an
	 * ancestor must supply. Pair with `resize="none"` and
	 * `.autoHeight=${false}` so the field's own bound wins over per-keystroke
	 * growth.
	 */
	@property({ type: Boolean, reflect: true }) fillHeight = false

	@property({ type: Boolean }) autoHeight = true

	@property({ type: String, reflect: true }) resize: 'none' | 'vertical' | 'horizontal' | 'both' = 'vertical'

	/** How text is wrapped when submitted (native `wrap` attribute). */
	@property({ type: String }) wrap: 'hard' | 'soft' = 'soft'

	@property({ type: String }) dirname: string | undefined

	// `required`, `disabled`, `readonly`, `error`, `validationMessage`, `hint`
	// come from the mixin. Textarea-specific extras only:
	@property({ type: Boolean, reflect: true }) spellcheck = false

	@property({ type: String, reflect: true }) align: 'left' | 'center' | 'right' = 'left'

	@property({ type: Boolean })
	public override autofocus!: boolean

	@property({ type: Number })
	public override tabIndex = 0

	@query('textarea') textareaElement!: HTMLTextAreaElement

	// Mixin's willUpdate calls `internals.setFormValue()` on value-change and
	// runs the required-empty / customError validity machinery, setting
	// `this.error`/`validationMessage` under `shouldShowError()` — the render
	// block below reads those. No validity override here (mirrors input.ts).

	connectedCallback() {
		super.connectedCallback()
		// Re-wire on RECONNECT: firstUpdated ran once and the per-connection
		// `disconnecting` tore down the prior connection's subs on the move-out;
		// `hasUpdated` is false before the first render (firstUpdated owns that),
		// true on every reparent.
		if (this.hasUpdated) this.wireTextareaEvents()
	}

	protected override firstUpdated(changedProps: Map<PropertyKey, unknown>) {
		// The mixin's own firstUpdated takes the first-rendered value as this
		// field's default. Skipped, `defaultValue` stays undefined and `dirty` is
		// permanently false — so the `validateOn: 'dirty'` gate never opens for
		// anything the user types, and `resetForm()` empties a field that
		// rendered with a value.
		super.firstUpdated?.(changedProps as never)

		// Autofocus is one-time only — never re-run on reconnect (mirrors input.ts).
		if (this.autofocus) {
			this.focus()
		}
		this.wireTextareaEvents()
	}

	/**
	 * Wires every `fromEvent(this.textareaElement, …)` subscription plus the
	 * autoHeight adjustment. Called from firstUpdated (shadow DOM ready) and
	 * again from connectedCallback on reconnect. Re-running autoHeight on
	 * reconnect is harmless — it only grows the height, never shrinks it — and
	 * correctly re-measures content that changed size while detached.
	 */
	private wireTextareaEvents() {
		if (this.autoHeight) {
			timer(0)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => this.adjustHeight())
		}
		// The platform's two events, kept apart exactly as <schmancy-input> keeps
		// them: `input` per keystroke, `change` only on the native commit.
		fromEvent<InputEvent>(this.textareaElement, 'input')
			.pipe(
				map(event => {
					// The native `input` is composed, so it reaches the host too. Left
					// running, a consumer's `@input` hears it beside ours — with `detail`
					// the UIEvent number rather than `{ value }`, and landing last.
					event.stopPropagation()
					return (event.target as HTMLTextAreaElement).value
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe(value => {
				this.value = value
				if (this.autoHeight) {
					this.adjustHeight()
				}
				this.dispatchEvent(
					new CustomEvent<EventDetails>('input', {
						detail: { value },
						bubbles: true,
						composed: true,
					}),
				)
			})
		fromEvent(this.textareaElement, 'change')
			.pipe(
				map(event => {
					event.stopPropagation()
					return (event.target as HTMLTextAreaElement).value
				}),
				distinctUntilChanged(),
				takeUntil(this.disconnecting),
			)
			.subscribe(value => {
				this.value = value
				if (this.autoHeight) {
					this.adjustHeight()
				}
				this.dispatchEvent(
					new CustomEvent<EventDetails>('change', {
						detail: { value },
						bubbles: true,
						composed: true,
					}),
				)
			})
		// Enter in a textarea inserts a newline — mid-entry, not a commit — so it
		// carries its own event and no `change`.
		fromEvent<KeyboardEvent>(this.textareaElement, 'keyup')
			.pipe(
				filter(event => event.key === 'Enter'),
				map(event => (event.target as HTMLTextAreaElement).value),
				distinctUntilChanged(),
				takeUntil(this.disconnecting),
			)
			.subscribe(value => {
				this.value = value
				this.dispatchEvent(
					new CustomEvent<EventDetails>('enter', {
						detail: { value },
						bubbles: true,
						composed: true,
					}),
				)
			})
		fromEvent<FocusEvent>(this.textareaElement, 'blur')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(() => this.markTouched())
	}

	get form() {
		return this.internals?.form
	}

	// `reportValidity`, `checkValidity`, `setCustomValidity` come from the mixin
	// — it gates `this.error`/`validationMessage` (read by render) via
	// `shouldShowError()`. Delegating to the inner native textarea bypassed that
	// gate and left the rendered error surface dead.

	public select() {
		return this.textareaRef.value?.select()
	}

	public setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none') {
		this.textareaRef.value?.setSelectionRange(start, end, direction)
	}

	public get selectionStart(): number | null {
		return this.textareaRef.value?.selectionStart ?? null
	}

	public get selectionEnd(): number | null {
		return this.textareaRef.value?.selectionEnd ?? null
	}

	public get selectionDirection(): 'forward' | 'backward' | 'none' | null {
		return this.textareaRef.value?.selectionDirection ?? null
	}

	public setRangeText(replacement: string) {
		this.textareaRef.value?.setRangeText(replacement)
	}

	public adjustHeight() {
		const textarea = this.textareaRef.value
		if (textarea) {
			// Only grow, never shrink
			const currentHeight = textarea.offsetHeight
			const scrollHeight = textarea.scrollHeight
			if (scrollHeight > currentHeight) {
				textarea.style.height = scrollHeight + 'px'
			}
		}
	}

	public validity(): ValidityState | undefined {
		return this.textareaRef.value?.validity
	}

	public override focus(
		options: FocusOptions = {
			preventScroll: true,
		},
	) {
		this.textareaRef.value?.focus(options)
		this.dispatchEvent(new Event('focus'))
	}

	public override click() {
		this.textareaRef.value?.click()
		this.dispatchEvent(new Event('click'))
	}

	public override blur() {
		this.textareaRef.value?.blur()
		this.dispatchEvent(new Event('blur'))
	}

	protected render(): unknown {
		const framed = this.fieldActions.filled
		const alignmentClasses = {
			'text-left': this.align === 'left',
			'text-center': this.align === 'center',
			'text-right': this.align === 'right',
		}
		const resizeClasses = {
			'resize-none': this.resize === 'none',
			'resize-y': this.resize === 'vertical',
			'resize-x': this.resize === 'horizontal',
			resize: this.resize === 'both',
		}
		// One chrome, two homes. Unframed, it sits on the <textarea> exactly as it
		// always has; framed, it moves to the wrapper and takes the focus-within
		// variant with it, because the focus the ring reports now lands on a child.
		// Drawn from `fieldChromeClasses` either way, so a change to the field
		// register reaches this field and <schmancy-input> together instead of
		// drifting between two hand-kept copies of one list.
		const textareaClasses = framed
			? {
					...alignmentClasses,
					...resizeClasses,
					'block w-full min-w-0 border-0 bg-transparent text-surface-on': true,
					'placeholder:text-muted focus:outline-hidden ring-0': true,
					'disabled:opacity-40 disabled:cursor-not-allowed': true,
					'caret-transparent cursor-pointer select-none': this.readonly,
					'flex-1 min-h-0': this.fillHeight,
					'px-4 pt-3 pb-1': true,
				}
			: {
					...fieldChromeClasses(this.error),
					...alignmentClasses,
					...resizeClasses,
					'caret-transparent focus:outline-hidden cursor-pointer select-none': this.readonly,
					'h-full': this.fillHeight,
					'px-4 py-3': true,
				}
		// `contents` unframed, not an absent element: the wrapper is always the same
		// DOM node, so the <textarea> inside it is never re-created when the slot
		// fills and the subscriptions `firstUpdated` bound to it stay bound. As
		// `contents` it contributes no box at all, which is what leaves the
		// unframed field the box it always rendered.
		const frameClasses = framed
			? {
					...fieldChromeClasses(this.error, true),
					'flex flex-col': true,
					// The wrapper paints the corner now, so a textarea scrolling its own
					// overflow inside it has to be cut to that corner rather than square
					// across it.
					'overflow-hidden': true,
					'flex-1 min-h-0': this.fillHeight,
				}
			: { contents: true }
		const fieldSizing = this.rows == null ? 'field-sizing: content;' : ''
		const labelClasses = {
			'block mb-1 font-medium text-sm': true,
			'opacity-40': this.disabled,
			// M3's resting label is `on-surface-variant`; `primary` is the focused state,
			// and spending it at rest collapses the two into one loud word.
			'text-surface-onVariant': !this.error,
			'text-error-default': this.error,
		}
		const containerClasses = {
			'w-full min-w-0': true,
			'flex flex-col h-full': this.fillHeight,
		}
		const hintId = `${this.a11yId}-hint`

		return html`
			<div class="${this.classMap(containerClasses)}">
				${when(
					this.label,
					() => html` <label class="${this.classMap(labelClasses)}" for=${this.id}> ${this.label} </label> `,
				)}

				<div class=${this.classMap(frameClasses)}>
					<textarea
						${ref(this.textareaRef)}
						.value=${this.value}
						.id=${this.id}
						.name=${this.name}
						.placeholder=${this.placeholder}
						.required=${this.required}
						class=${this.classMap(textareaClasses)}
						style=${fieldSizing}
						.disabled=${this.disabled}
						minlength=${ifDefined(this.minlength)}
						maxlength=${ifDefined(this.maxlength)}
						.readonly=${this.readonly}
						.spellcheck=${this.spellcheck}
						cols=${ifDefined(this.cols)}
						rows=${ifDefined(this.rows)}
						wrap=${ifDefined(this.wrap)}
						dirname=${ifDefined(this.dirname)}
						aria-invalid=${this.error ? 'true' : 'false'}
						aria-required=${this.required ? 'true' : 'false'}
						aria-describedby=${this.hint || (this.error && this.validationMessage) ? hintId : nothing}
						aria-label=${ifDefined(this.ariaLabel || this.label || this.placeholder || undefined)}
					></textarea>

					<!--
						After the <textarea> in DOM order, so Tab leaves the text and lands
						here. Empty, the row carries no padding and measures zero.
					-->
					<div
						${ref(this.fieldActions.observe)}
						part="actions"
						class=${this.classMap({
							'flex items-center justify-end gap-2': true,
							'px-4 pb-3': framed,
						})}
					>
						<slot name="actions" @slotchange=${this.fieldActions.sync}></slot>
					</div>
				</div>

				${when(
					this.hint || (this.error && this.validationMessage),
					() => html`
						<div
							id=${hintId}
							class=${this.classMap({ 'mt-1 text-sm wrap-anywhere': true, 'text-error-default': this.error, 'text-surface-onVariant': !this.error })}
							role=${ifDefined(this.error ? 'alert' : undefined)}
						>
							${this.error && this.validationMessage ? this.validationMessage : this.hint}
						</div>
					`,
				)}
			</div>
		`
	}
}

type EventDetails = {
	value: string
}

export type SchmancyTextareaChangeEvent = CustomEvent<EventDetails>

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-textarea': SchmancyTextarea
	}
}
